import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch, type FieldPath } from 'react-hook-form';

import { FileKindEnum } from '../../types/enums';
import type { AdMediaType } from '../../types/ads';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import {
  getPropertyAdPublishFieldErrors,
  type PropertyAdPublishFieldError,
} from './propertyAdEditorPublishValidation';

export const PROPERTY_AD_MIN_PUBLISH_PHOTOS = 4;
export const PROPERTY_AD_PHOTO_PUBLISH_ERROR = 'Upload at least 4 photos before publishing.';
export const PROPERTY_AD_PUBLISH_VALIDATION_SUMMARY =
  'Please review the highlighted fields before publishing.';

interface UsePropertyAdEditorLiveValidationOptions {
  fieldOrder: readonly FieldPath<PropertyAdEditorFormValues>[];
  media: AdMediaType[];
  onPhotoPublishErrorChange?: (message: string) => void;
}

export interface PropertyAdPublishValidationResult {
  areFieldsValid: boolean;
  hasEnoughPhotos: boolean;
  invalidFields: Set<FieldPath<PropertyAdEditorFormValues>>;
  isValid: boolean;
}

const indexErrorsByField = (errors: PropertyAdPublishFieldError[]) =>
  new Map(errors.map((error) => [error.field, error]));

export const usePropertyAdEditorLiveValidation = ({
  fieldOrder,
  media,
  onPhotoPublishErrorChange,
}: UsePropertyAdEditorLiveValidationOptions) => {
  const { clearErrors, control, getFieldState, getValues, setError, trigger } =
    useFormContext<PropertyAdEditorFormValues>();
  const watchedValues = useWatch({ control });
  const [validationSummary, setValidationSummary] = useState('');
  const activeManualFieldsRef = useRef(new Set<FieldPath<PropertyAdEditorFormValues>>());
  const activeRhfFieldsRef = useRef<FieldPath<PropertyAdEditorFormValues>[]>([]);
  const fullPublishValidationActiveRef = useRef(false);
  const photoValidationActiveRef = useRef(false);
  const revalidationGenerationRef = useRef(0);

  const hasEnoughPhotos = useCallback(
    (currentMedia: AdMediaType[] = media) =>
      currentMedia.filter((item) => item.kind === FileKindEnum.Image).length >=
      PROPERTY_AD_MIN_PUBLISH_PHOTOS,
    [media]
  );

  const applyManualErrors = useCallback(
    (errors: PropertyAdPublishFieldError[], expandActiveFields: boolean) => {
      const errorsByField = indexErrorsByField(errors);

      if (expandActiveFields) {
        errors.forEach(({ field }) => activeManualFieldsRef.current.add(field));
      }

      activeManualFieldsRef.current.forEach((field) => {
        const nextError = errorsByField.get(field);
        const currentError = getFieldState(field).error;

        if (nextError) {
          if (currentError?.type !== 'publish' || currentError.message !== nextError.message) {
            setError(field, { message: nextError.message, type: 'publish' });
          }
        } else if (currentError?.type === 'publish') {
          clearErrors(field);
        }
      });

      return errors.filter(({ field }) => activeManualFieldsRef.current.has(field));
    },
    [clearErrors, getFieldState, setError]
  );

  const revalidateActiveErrors = useCallback(async () => {
    if (
      !fullPublishValidationActiveRef.current &&
      activeManualFieldsRef.current.size === 0 &&
      activeRhfFieldsRef.current.length === 0
    ) {
      return;
    }

    const generation = ++revalidationGenerationRef.current;
    const rhfFields = activeRhfFieldsRef.current;
    const activeRhfFieldsAreValid = rhfFields.length === 0 ? true : await trigger(rhfFields);

    if (generation !== revalidationGenerationRef.current) return;

    const manualErrors = applyManualErrors(
      getPropertyAdPublishFieldErrors(getValues()),
      fullPublishValidationActiveRef.current
    );
    const photoErrorIsActive = photoValidationActiveRef.current && !hasEnoughPhotos();

    if (photoValidationActiveRef.current) {
      onPhotoPublishErrorChange?.(photoErrorIsActive ? PROPERTY_AD_PHOTO_PUBLISH_ERROR : '');
    }

    if (!fullPublishValidationActiveRef.current) return;

    if (photoErrorIsActive) {
      setValidationSummary(PROPERTY_AD_PHOTO_PUBLISH_ERROR);
    } else if (!activeRhfFieldsAreValid || manualErrors.length > 0) {
      setValidationSummary(PROPERTY_AD_PUBLISH_VALIDATION_SUMMARY);
    } else {
      setValidationSummary('');
    }
  }, [applyManualErrors, getValues, hasEnoughPhotos, onPhotoPublishErrorChange, trigger]);

  useEffect(() => {
    void revalidateActiveErrors();
  }, [media, revalidateActiveErrors, watchedValues]);

  const validatePublish = useCallback(
    async (currentMedia?: AdMediaType[]): Promise<PropertyAdPublishValidationResult> => {
      revalidationGenerationRef.current += 1;
      clearErrors();

      const registeredFieldsAreValid = await trigger();
      const activeRhfFields = fieldOrder.filter((field) => getFieldState(field).invalid);
      const manualErrors = getPropertyAdPublishFieldErrors(getValues());

      activeRhfFieldsRef.current = activeRhfFields;
      activeManualFieldsRef.current = new Set(manualErrors.map(({ field }) => field));
      fullPublishValidationActiveRef.current = true;
      photoValidationActiveRef.current = true;
      applyManualErrors(manualErrors, true);

      const enoughPhotos = hasEnoughPhotos(currentMedia);
      onPhotoPublishErrorChange?.(enoughPhotos ? '' : PROPERTY_AD_PHOTO_PUBLISH_ERROR);

      if (!enoughPhotos) {
        setValidationSummary(PROPERTY_AD_PHOTO_PUBLISH_ERROR);
      } else if (!registeredFieldsAreValid || manualErrors.length > 0) {
        setValidationSummary(PROPERTY_AD_PUBLISH_VALIDATION_SUMMARY);
      } else {
        setValidationSummary('');
      }

      const invalidFields = new Set<FieldPath<PropertyAdEditorFormValues>>(activeRhfFields);
      manualErrors.forEach(({ field }) => invalidFields.add(field));

      return {
        areFieldsValid: registeredFieldsAreValid && manualErrors.length === 0,
        hasEnoughPhotos: enoughPhotos,
        invalidFields,
        isValid: registeredFieldsAreValid && manualErrors.length === 0 && enoughPhotos,
      };
    },
    [
      applyManualErrors,
      clearErrors,
      fieldOrder,
      getFieldState,
      getValues,
      hasEnoughPhotos,
      onPhotoPublishErrorChange,
      trigger,
    ]
  );

  return { validatePublish, validationSummary };
};
